import { workspaceFetch } from '../../../../shared/platform/business-workspaces';
import { monitoringEnabled } from '../../monitoring/adapters/equipment-monitoring-adapter';
import { EquipmentMonitoring, MonitoringDetail } from '../../monitoring/ui/EquipmentMonitoring';
/**
 * The 05 SYN LIVE equipment pages, driven entirely by real server responses.
 * LOCAL SYNTHETIC / NON-OPERATIONAL. NOT a production equipment register.
 *
 * <p>WHY IT IS MOUNTED INSIDE THE CONFIRMED CENTRE RATHER THAN REPLACING IT. The 05 page, its
 * navigation and its three-column detail are already confirmed and reviewed. This component adds the
 * SYN LIVE sections to that page and leaves the DEMO prototype exactly where it is, so the two never
 * have to be told apart by a reader: DEMO is labelled DEMO and never calls a server, and LIVE shows
 * only what a server response carried.
 *
 * <p>WHAT IT NEVER DOES. It never fills a failed read with a fixture, never shows a previous
 * snapshot as the current one while a request is in flight, and never renders an unpublished asset
 * in the published catalogue. A read that fails clears what is on screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { apiBaseOrigin } from '../../../../shared/config/api-origin';
import type { ContentRef } from '../../../../shared/content-media/ContentMedia';
import { ContentEditor, ContentViewer } from '../../../../shared/content-media/ContentMedia';
import { SynExactMatching } from '../../../scenario-center/ui/SynExactMatching';
import { SynAssetAssessment } from '../../assessment/ui/SynAssessment';
import { createLiveLocalAssetWorkflowAdapter } from '../adapters/live-local-asset-workflow-adapter';
import type {
  AssetReviewCaseView,
  AssetState,
  AssetView,
  DeclarationInput,
  DeclarationView,
  LinkKind,
  LocalAssetWorkflowPort,
  ProductPublishedRefInput,
  PublishedProductView,
  ServerOutcome,
} from '../adapters/local-asset-workflow-port';
import './asset-data-management-prototype.css';

/** Explicit local development opt-in. Public hosting and production builds cannot enable writes. */
export function synLiveAssetEnabled(
  dev = import.meta.env.DEV,
  location = window.location as Location | undefined,
) {
  return Boolean(
    dev &&
    location &&
    ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) &&
    new URLSearchParams(location.search).get('synLive') === '1',
  );
}

interface Session {
  readonly session: string;
  readonly csrf: string;
  readonly account: string;
  readonly expiresAt: string;
}

export function parseAssetSession(value: unknown): Session {
  if (
    typeof value !== 'object' ||
    value === null ||
    Object.keys(value).sort().join(',') !== 'account,csrf,expiresAt,session'
  ) {
    throw new Error('Invalid session response');
  }
  const fields = value as Record<string, unknown>;
  for (const name of ['session', 'csrf', 'account', 'expiresAt']) {
    if (typeof fields[name] !== 'string' || !fields[name])
      throw new Error('Invalid session response');
  }
  if (!Number.isFinite(Date.parse(fields.expiresAt as string)))
    throw new Error('Invalid session expiry');
  return fields as unknown as Session;
}

export function outcomeText(outcome: ServerOutcome<unknown>): string {
  if (outcome.kind === 'OK') return '服务端已确认';
  if (outcome.kind === 'REFUSED') {
    return `服务端拒绝 HTTP ${outcome.httpStatus} · ${outcome.code}${outcome.field ? ` · ${outcome.field}` : ''}`;
  }
  if (outcome.kind === 'UNREACHABLE') return '结果未确认。请读回；写入可使用原请求与原幂等键重试。';
  return '响应契约异常，结果未确认。请读回或重试原请求。';
}

const STATE_LABELS: Record<AssetState, string> = {
  DRAFT: '草稿',
  SUBMITTED: '审核中',
  PUBLISHED: '已发布',
};

const LINK_LABELS: Record<LinkKind, string> = {
  LINKED: '已关联 06 已发布产品精确版本',
  NOT_LINKED: '明确未关联型号',
  UNKNOWN: '型号未知',
};

const emptyDeclaration: DeclarationInput = {
  displayName: '',
  category: '',
  internalRef: '',
  purpose: '',
  configurationDeclaration: '',
  sourceNote: '',
  productLink: { kind: 'NOT_LINKED' },
};

/** A stored declaration as an editable form value. The digest is not part of what is edited. */
function toDeclarationInput(declaration: DeclarationView): DeclarationInput {
  return {
    displayName: declaration.displayName,
    category: declaration.category,
    internalRef: declaration.internalRef,
    purpose: declaration.purpose,
    configurationDeclaration: declaration.configurationDeclaration,
    sourceNote: declaration.sourceNote,
    ...(declaration.contentJson ? { contentJson: declaration.contentJson } : {}),
    productLink: declaration.productLink,
  };
}

/** A fresh idempotency key per USER ACTION, not per render. */
function newKey(prefix: string): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `${prefix}-${random}`;
}

interface Live {
  readonly port: LocalAssetWorkflowPort;
  readonly session: string;
  readonly csrf: string;
  readonly account: string;
  readonly busy: boolean;
  readonly notice: string;
  readonly setNotice: (value: string) => void;
  readonly blocked: boolean;
  readonly run: (label: string, action: () => Promise<void>) => Promise<void>;
}

function useLive(port: LocalAssetWorkflowPort, auth: Session | null): Live {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const blocked = auth === null;
  const run = useCallback(async (label: string, action: () => Promise<void>) => {
    setBusy(true);
    setNotice(`${label}…`);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }, []);
  return {
    port,
    session: auth?.session ?? '',
    csrf: auth?.csrf ?? '',
    account: auth?.account ?? '',
    busy,
    notice,
    setNotice,
    blocked,
    run,
  };
}

/**
 * The session panel. The credential is a synthetic fixture token, typed by the operator, and it is
 * never stored anywhere but this component's state.
 */
function SessionPanel({
  onAuthenticated,
  account,
  expiresAt,
}: {
  readonly onAuthenticated: (session: Session | null) => void;
  readonly account: string;
  readonly expiresAt: string;
}) {
  const [credential, setCredential] = useState('');
  const [failure, setFailure] = useState('');
  const [busy, setBusy] = useState(false);
  const origin = apiBaseOrigin();

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setFailure('');
    try {
      const response = await workspaceFetch(`${origin}/management/local-asset/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
        credentials: 'omit',
        redirect: 'error',
        cache: 'no-store',
      });
      if (!response.ok) {
        setFailure(`会话未建立（HTTP ${response.status}）`);
        onAuthenticated(null);
        return;
      }
      onAuthenticated(parseAssetSession(await response.json()));
    } catch (error) {
      setFailure(error instanceof Error ? error.message : '会话请求失败');
      onAuthenticated(null);
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    if (!account) return;
    try {
      await workspaceFetch(`${origin}/management/local-asset/session`, {
        method: 'DELETE',
        credentials: 'omit',
        redirect: 'error',
        cache: 'no-store',
      });
    } finally {
      onAuthenticated(null);
    }
  }

  return (
    <section className="asset-ux" aria-label="05 SYN 合成会话">
      <div className="asset-ux__tools">
        <div>
          <strong>05 装备管理 · SYN LIVE</strong>
          <p>合成身份 · 真实数据库 · 非正式登记，不构成设备或行动授权</p>
        </div>
        {account ? (
          <>
            <span data-syn-account>
              已登录：{account} · 会话到期 {expiresAt}
            </span>
            <button type="button" onClick={() => void logout()}>
              退出合成会话
            </button>
          </>
        ) : (
          <form onSubmit={(event) => void login(event)}>
            <label>
              合成会话凭据
              <input
                aria-label="合成会话凭据"
                value={credential}
                onChange={(event) => setCredential(event.target.value)}
                autoComplete="off"
              />
            </label>
            <button type="submit" disabled={busy || !credential}>
              建立合成会话
            </button>
          </form>
        )}
      </div>
      {failure && (
        <p role="status" className="asset-ux__feedback" data-syn-session-failure>
          {failure}
        </p>
      )}
    </section>
  );
}

/** The registration form. Country and agency are shown, never entered. */
function DeclarationForm({
  live,
  scope,
  products,
  initial,
  contentReference,
  submitLabel,
  onSave,
  onCancel,
}: {
  readonly live: Live;
  readonly scope: {
    readonly country: string;
    readonly agency: string;
    readonly reviewScope: string;
  };
  readonly products: readonly PublishedProductView[];
  readonly initial: DeclarationInput;
  readonly contentReference?: ContentRef;
  readonly submitLabel: string;
  /**
   * WHAT TO DO WITH THE DECLARATION, DECIDED BY THE CALLER.
   *
   * <p>The form does not know whether it is creating a unit or revising one, and it must not guess:
   * the two are different Owner commands with different versions and different effects. The caller
   * holds the asset it is revising, so the caller supplies the action.
   */
  readonly onSave: (declaration: DeclarationInput) => void;
  readonly onCancel: () => void;
}) {
  const [fields, setFields] = useState<DeclarationInput>(initial);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const found: Record<string, string> = {};
    for (const [key, label] of [
      ['displayName', '装备名称'],
      ['category', '装备类别'],
      ['purpose', '用途说明'],
      ['configurationDeclaration', '实际配置声明'],
      ['sourceNote', '资料来源备注'],
    ] as const) {
      if (!fields[key].trim()) found[key] = `${label}为必填。`;
    }
    if (fields.productLink.kind === 'LINKED' && !fields.productLink.product) {
      found['productLink'] = '选择“已关联”时必须选定一个精确发布版本。';
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  }

  return (
    /*
      THE FORM IS WRAPPED IN `.asset-ux` ON PURPOSE. The confirmed prototype styles every control
      under that class, and a bare `form.asset-ux__layout` outside it gets the grid but none of the
      label, field and hint rules — which is exactly how labels ended up running into the next field
      on screen. Reusing the wrapper reuses the confirmed styling instead of inventing a second one.
    */
    <section className="asset-ux" aria-label="装备申报表单">
      <form
        noValidate
        className="asset-ux__layout"
        onSubmit={(event) => {
          event.preventDefault();
          if (mediaBusy || !validate()) return;
          onSave(fields);
        }}
      >
        <div>
          <fieldset>
            <legend>基本资料与声明</legend>
            {/*
            THE REVIEW SCOPE IS SHOWN BECAUSE IT MUST BE TRANSCRIBED. The 08 intake form asks the
            administrator to type the exact revision including its review scope, and a value the
            agency cannot read anywhere would make that transcription a guess.
          */}
            <p data-syn-scope>
              机构范围（服务端会话）：{scope.country} · {scope.agency} · 审核范围{' '}
              {scope.reviewScope}
            </p>
            {(
              [
                ['displayName', '装备名称', '最多 255 字符，必填。'],
                ['category', '装备类别', '最多 128 字符，必填。'],
                ['internalRef', '机构内部编号（可选）', '同一机构范围内唯一；留空表示没有编号。'],
                ['purpose', '用途说明', '必填。用途声明不构成已验证能力。'],
              ] as const
            ).map(([key, label, hint]) => (
              <label key={key} htmlFor={`syn-asset-${key}`}>
                {label}
                <input
                  id={`syn-asset-${key}`}
                  value={fields[key]}
                  aria-invalid={errors[key] !== undefined}
                  onChange={(event) => setFields({ ...fields, [key]: event.target.value })}
                />
                <small>{hint}</small>
                {errors[key] && <span className="asset-ux__error">{errors[key]}</span>}
              </label>
            ))}
            {(
              [
                ['configurationDeclaration', '实际配置声明', '机构对本机的声明，未经任何方验证。'],
                ['sourceNote', '资料来源备注', '注明来源与使用限制。'],
              ] as const
            ).map(([key, label, hint]) => (
              <label key={key} htmlFor={`syn-asset-${key}`}>
                {label}
                <textarea
                  id={`syn-asset-${key}`}
                  value={fields[key]}
                  aria-invalid={errors[key] !== undefined}
                  onChange={(event) => setFields({ ...fields, [key]: event.target.value })}
                />
                <small>{hint}</small>
                {errors[key] && <span className="asset-ux__error">{errors[key]}</span>}
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>关联产品型号</legend>
            <label htmlFor="syn-asset-link">
              型号关联
              <select
                id="syn-asset-link"
                value={fields.productLink.kind}
                onChange={(event) => {
                  const kind = event.target.value as LinkKind;
                  setFields({
                    ...fields,
                    productLink: kind === 'LINKED' ? { kind } : { kind },
                  });
                }}
              >
                {(Object.keys(LINK_LABELS) as LinkKind[]).map((kind) => (
                  <option key={kind} value={kind}>
                    {LINK_LABELS[kind]}
                  </option>
                ))}
              </select>
            </label>
            {errors['productLink'] && (
              <span className="asset-ux__error">{errors['productLink']}</span>
            )}
            {fields.productLink.kind === 'LINKED' && (
              <>
                <label htmlFor="syn-asset-product">
                  06 已发布产品精确版本
                  <select
                    id="syn-asset-product"
                    value={
                      fields.productLink.product
                        ? `${fields.productLink.product.productId}#${fields.productLink.product.publishedVersion}`
                        : ''
                    }
                    onChange={(event) => {
                      const chosen = products.find(
                        (item) =>
                          `${item.productId}#${item.publishedVersion}` === event.target.value,
                      );
                      const product: ProductPublishedRefInput | undefined = chosen
                        ? {
                            productId: chosen.productId,
                            country: chosen.country,
                            supplier: chosen.supplier,
                            publishedVersion: chosen.publishedVersion,
                            digest: chosen.digest,
                          }
                        : undefined;
                      setFields({
                        ...fields,
                        productLink: product ? { kind: 'LINKED', product } : { kind: 'LINKED' },
                      });
                    }}
                  >
                    <option value="">请选择一个已发布版本</option>
                    {products.map((item) => (
                      <option
                        key={`${item.productId}#${item.publishedVersion}`}
                        value={`${item.productId}#${item.publishedVersion}`}
                      >
                        {item.modelName} · {item.category} · 版本 {item.publishedVersion} ·{' '}
                        {item.digest.slice(0, 12)}…
                      </option>
                    ))}
                  </select>
                </label>
                {fields.productLink.product && (
                  <dl className="asset-ux__summary" data-syn-product-ref>
                    <div>
                      <dt>产品 ID</dt>
                      <dd>{fields.productLink.product.productId}</dd>
                    </div>
                    <div>
                      <dt>发布版本</dt>
                      <dd>{fields.productLink.product.publishedVersion}</dd>
                    </div>
                    <div>
                      <dt>厂家声明来源</dt>
                      <dd>
                        {
                          products.find(
                            (item) =>
                              item.productId === fields.productLink.product?.productId &&
                              item.publishedVersion ===
                                fields.productLink.product?.publishedVersion,
                          )?.source
                        }
                      </dd>
                    </div>
                  </dl>
                )}
                <p>
                  厂家声明 ≠ 本机实际配置声明 ≠ 已验证能力。关联只证明 06 的该精确版本存在；提交时由
                  05 通过 06 读取核验。
                </p>
              </>
            )}
          </fieldset>
          <ContentEditor
            owner="05"
            auth={live}
            value={fields.contentJson ?? ''}
            onChange={(contentJson) => setFields({ ...fields, contentJson })}
            onBusy={setMediaBusy}
            reference={contentReference}
          />
          <div className="asset-ux__actions">
            <button type="submit" disabled={live.busy || mediaBusy}>
              {submitLabel}
            </button>
            <button type="button" onClick={onCancel}>
              放弃编辑
            </button>
          </div>
        </div>
        <aside>
          <h3>当前填写摘要</h3>
          <dl className="asset-ux__summary">
            <div>
              <dt>装备名称</dt>
              <dd>{fields.displayName || '尚未填写'}</dd>
            </div>
            <div>
              <dt>型号关联</dt>
              <dd>{LINK_LABELS[fields.productLink.kind]}</dd>
            </div>
          </dl>
          <p>资料发布 ≠ 能力验证 ≠ 在线/READY ≠ 调度授权。</p>
          <p>此表单不签发装备身份；资产 ID 由 05 在保存时签发。</p>
        </aside>
      </form>
    </section>
  );
}

function AssetDetail({
  live,
  asset,
  scope,
  reviews,
  onChanged,
  onEdit,
  onBack,
}: {
  readonly live: Live;
  readonly asset: AssetView;
  readonly scope: {
    readonly country: string;
    readonly agency: string;
    readonly reviewScope: string;
  };
  readonly reviews: readonly AssetReviewCaseView[];
  readonly onChanged: (asset: AssetView) => void;
  readonly onEdit: (asset: AssetView) => void;
  readonly onBack: () => void;
}) {
  const current =
    asset.submissions.length > 0 ? asset.submissions[asset.submissions.length - 1] : null;
  const [readback, setReadback] = useState('');
  const returned = reviews.find((item) => item.decision?.outcome === 'CHANGES_REQUESTED');

  return (
    <section className="asset-ux" aria-label="装备详情">
      <div className="asset-ux__detail-heading">
        <div>
          <h3>{asset.declaration.displayName}</h3>
          <p data-syn-asset-state>
            {STATE_LABELS[asset.state]} · 聚合版本 {asset.version} · {asset.assetId}
          </p>
        </div>
        <button type="button" onClick={onBack}>
          返回我的提交
        </button>
      </div>
      <p className="asset-ux__notice">{live.notice}</p>
      {monitoringEnabled() && asset.publications.length > 0 && (
        <MonitoringDetail key={asset.assetId} auth={live} assetId={asset.assetId} />
      )}
      <SynAssetAssessment
        key={asset.assetId}
        auth={live}
        assetId={asset.assetId}
        publications={asset.publications}
      />
      {/*
        THE SCOPE IS SHOWN HERE TOO, AND THIS IS THE PLACE IT MATTERS MOST. The 08 intake asks the
        administrator to transcribe the exact revision — country, agency and review scope included —
        and the detail view is where the agency reads the revision it just submitted. A value that is
        only visible on a different tab is a value the administrator has to guess.
      */}
      <p data-syn-scope>
        机构范围（服务端会话）：{scope.country} · {scope.agency} · 审核范围 {scope.reviewScope}
      </p>
      <dl className="asset-ux__summary">
        <div>
          <dt>机构范围</dt>
          <dd>
            {asset.country} · {asset.agency}
          </dd>
        </div>
        <div>
          <dt>机构内部编号</dt>
          <dd>{asset.declaration.internalRef || '未填写'}</dd>
        </div>
        <div>
          <dt>用途说明</dt>
          <dd>{asset.declaration.purpose}</dd>
        </div>
        <div>
          <dt>实际配置声明</dt>
          <dd>{asset.declaration.configurationDeclaration}</dd>
        </div>
        <div>
          <dt>资料来源备注</dt>
          <dd>{asset.declaration.sourceNote}</dd>
        </div>
        <div>
          <dt>型号关联</dt>
          <dd>
            {LINK_LABELS[asset.declaration.productLink.kind]}
            {asset.declaration.productLink.product
              ? ` · ${asset.declaration.productLink.product.productId} v${asset.declaration.productLink.product.publishedVersion}`
              : ''}
          </dd>
        </div>
        <div>
          <dt>声明摘要</dt>
          <dd>
            <details className="cm-audit">
              <summary>查看完整声明摘要</summary>
              <code data-syn-declaration-digest>{asset.declaration.digest}</code>
            </details>
          </dd>
        </div>
        <div>
          <dt>本机能力</dt>
          <dd>
            {asset.capabilityStatus} / {asset.verificationStatus}
          </dd>
        </div>
      </dl>

      <ContentViewer
        owner="05"
        auth={live}
        reference={{
          subjectId: asset.assetId,
          revision: asset.version,
          digest: asset.declaration.digest,
          view: 'draft',
        }}
      />
      {asset.publications.map((p) => (
        <ContentViewer
          key={p.publishedVersion}
          owner="05"
          auth={live}
          title={`已发布装备版本 ${p.publishedVersion} 的资料`}
          reference={{
            subjectId: asset.assetId,
            revision: p.publishedVersion,
            digest: p.digest,
            view: 'published',
          }}
        />
      ))}
      {asset.declaration.productLink.product && (
        <ContentViewer
          owner="06"
          auth={live}
          title="精确关联产品版本的资料（厂家声明）"
          reference={{
            subjectId: asset.declaration.productLink.product.productId,
            revision: asset.declaration.productLink.product.publishedVersion,
            digest: asset.declaration.productLink.product.digest,
            view: 'published',
            access: 'product-link',
          }}
        />
      )}
      {returned?.decision && (
        <p role="status" data-syn-return-reason>
          08 退回原因（{returned.decision.decisionId}）：{returned.decision.reason}
        </p>
      )}
      <section aria-label="版本记录">
        <h4>版本记录</h4>
        <p>v1 与后续版本各自保留；新版本不覆盖旧版本。</p>
        <ul>
          {asset.submissions.map((submission) => (
            <li key={submission.revision}>
              提交 r{submission.revision} · 摘要 {submission.digest.slice(0, 12)}… ·{' '}
              {submission.submittedAt}
            </li>
          ))}
        </ul>
        <ul>
          {asset.publications.map((publication) => (
            <li key={publication.publishedVersion}>
              发布 v{publication.publishedVersion} · 来自 r{publication.submissionRevision} · 决定{' '}
              {publication.decisionId} · {publication.publishedAt}
            </li>
          ))}
        </ul>
      </section>
      <div className="asset-ux__actions">
        {asset.state === 'DRAFT' && (
          <button
            type="button"
            disabled={live.busy}
            onClick={() =>
              void live.run('提交审核', async () => {
                const outcome = await live.port.submit({
                  session: live.session,
                  idempotencyKey: newKey('asset-submit'),
                  assetId: asset.assetId,
                  expectedVersion: asset.version,
                });
                live.setNotice(outcomeText(outcome));
                const reread = await live.port.read({
                  session: live.session,
                  assetId: asset.assetId,
                });
                if (reread.kind === 'OK') onChanged(reread.value);
              })
            }
          >
            提交审核
          </button>
        )}
        {asset.state === 'SUBMITTED' && (
          <>
            <button
              type="button"
              disabled={live.busy || !returned?.decision}
              onClick={() =>
                void live.run('退回后重新修订', async () => {
                  const outcome = await live.port.resume({
                    session: live.session,
                    idempotencyKey: newKey('asset-resume'),
                    assetId: asset.assetId,
                    expectedVersion: asset.version,
                    decisionId: returned!.decision!.decisionId,
                  });
                  live.setNotice(outcomeText(outcome));
                  // REOPENING IS NOT REVISING. Resume only returns the unit to draft; the agency then
                  // has to actually change something, which is why the editor opens here rather than
                  // the page resubmitting what was already returned.
                  if (outcome.kind === 'OK') {
                    onChanged(outcome.value);
                    onEdit(outcome.value);
                  }
                })
              }
            >
              退回后重新修订
            </button>
            <span>
              {returned?.decision
                ? '已收到退回意见，可重新修订并再次提交。'
                : '等待 08 审核。审核通过只产生审核决定；发布由 05 Owner 独立执行。'}
            </span>
          </>
        )}
        {asset.state === 'PUBLISHED' && (
          <button type="button" disabled={live.busy} onClick={() => onEdit(asset)}>
            建立新修订
          </button>
        )}
        {asset.state === 'DRAFT' && (
          <button
            type="button"
            data-syn-asset-edit
            disabled={live.busy}
            onClick={() => onEdit(asset)}
          >
            编辑申报
          </button>
        )}
      </div>
      {asset.publications.length > 0 && (
        <section aria-label="精确读回">
          <h4>精确读回</h4>
          <p>精确读回只确认“该精确发布版本仍以同一摘要存在”，不确认设备状态或能力。</p>
          {/*
            ONE CONTROL PER PUBLISHED VERSION, NOT ONLY THE NEWEST. "v1 stays exactly readable after
            v2 exists" is a claim the agency has to be able to check on the page, and a page that only
            ever reads back the latest version cannot check it — it would show a version that was
            never in question.
          */}
          {asset.publications.map((publication) => (
            <button
              key={publication.publishedVersion}
              type="button"
              disabled={live.busy}
              onClick={() =>
                void live.run(`精确读回 v${publication.publishedVersion}`, async () => {
                  const outcome = await live.port.readExact({
                    session: live.session,
                    assetId: publication.assetId,
                    publishedVersion: publication.publishedVersion,
                    country: publication.country,
                    agency: publication.agency,
                    digest: publication.digest,
                  });
                  setReadback(
                    outcome.kind === 'OK'
                      ? `v${publication.publishedVersion} 读回状态 ${outcome.value.status}`
                      : `v${publication.publishedVersion} ${outcomeText(outcome)}`,
                  );
                })
              }
            >
              核对 v{publication.publishedVersion} 精确读回
            </button>
          ))}
          {readback && <p data-syn-readback>{readback}</p>}
        </section>
      )}
      {current === null && <p>尚无提交版本。</p>}
    </section>
  );
}

/**
 * The LIVE consumer for the 05 centre. Held once per page, so the 登记装备 / 我的提交 / 装备目录
 * sections share one session and one server view.
 */
export function SynLiveLocalAsset() {
  const [auth, setAuth] = useState<Session | null>(null);
  const [scope, setScope] = useState<{
    country: string;
    agency: string;
    reviewScope: string;
  } | null>(null);
  const [products, setProducts] = useState<readonly PublishedProductView[]>([]);
  const [own, setOwn] = useState<readonly AssetView[]>([]);
  const [catalog, setCatalog] = useState<readonly AssetView[]>([]);
  const [selected, setSelected] = useState<AssetView | null>(null);
  const [reviews, setReviews] = useState<readonly AssetReviewCaseView[]>([]);
  const [section, setSection] = useState<'form' | 'mine' | 'catalog' | 'monitoring' | 'matching'>(
    monitoringEnabled() ? 'monitoring' : 'mine',
  );
  /**
   * The asset being revised, or null.
   *
   * <p>WHY IT IS SEPARATE FROM `selected`. Selecting an asset is looking at it; editing one is
   * changing it, and the two must not be the same state or a stray click would put the agency into a
   * form it did not ask for. It also carries the VERSION the edit is against, so the save is a
   * compare-and-set on the revision the form was opened at rather than on whatever is current.
   */
  const [editor, setEditor] = useState<AssetView | null>(null);
  /**
   * The CSRF value, read at REQUEST time rather than captured at construction.
   *
   * <p>WHY IT IS A REF AND NOT A DEPENDENCY. The adapter is built once per page, and a session
   * arrives after that — so a port constructed with the session baked in would send no CSRF header
   * on the first request and be refused. That is exactly the failure this shape prevents: the header
   * is read when a mutation is actually made, from whatever session is current then.
   */
  const csrfRef = useRef<string>('');
  csrfRef.current = auth?.csrf ?? '';
  const port = useMemo(
    () =>
      createLiveLocalAssetWorkflowAdapter({
        baseOrigin: apiBaseOrigin(),
        csrf: () => csrfRef.current,
      }),
    [],
  );
  const live = useLive(port, auth);

  const refresh = useCallback(
    async (session: Session) => {
      const [scopeOutcome, ownOutcome, catalogOutcome, productOutcome] = await Promise.all([
        port.scope({ session: session.session }),
        port.ownAssets({ session: session.session }),
        port.catalog({ session: session.session }),
        port.productVersions({ session: session.session }),
      ]);
      if (scopeOutcome.kind === 'OK') setScope(scopeOutcome.value);
      else setScope(null);
      // A failed read CLEARS what is on screen. Leaving a previous list up while a request failed
      // would show the operator a set of assets this session may no longer be entitled to see.
      setOwn(ownOutcome.kind === 'OK' ? ownOutcome.value : []);
      setCatalog(catalogOutcome.kind === 'OK' ? catalogOutcome.value : []);
      setProducts(productOutcome.kind === 'OK' ? productOutcome.value : []);
    },
    [port],
  );

  useEffect(() => {
    if (auth) void refresh(auth);
    else {
      setScope(null);
      setOwn([]);
      setCatalog([]);
      setProducts([]);
      setSelected(null);
    }
  }, [auth, refresh]);

  useEffect(() => {
    if (!auth || !selected) {
      setReviews([]);
      return;
    }
    void port.assetReviews({ session: auth.session, assetId: selected.assetId }).then((outcome) => {
      setReviews(outcome.kind === 'OK' ? outcome.value : []);
    });
  }, [auth, port, selected]);

  return (
    <section
      className={monitoringEnabled() ? 'eqm-workflow' : undefined}
      aria-label="05 SYN LIVE 装备工作流"
    >
      <SessionPanel
        onAuthenticated={(session) => {
          setAuth(session);
          setSelected(null);
        }}
        account={auth?.account ?? ''}
        expiresAt={auth?.expiresAt ?? ''}
      />
      {auth && (
        <div className="ca-center__workspace-tabs" role="tablist" aria-label="SYN LIVE 装备工作区">
          {(
            [
              ['form', '登记装备'],
              ['mine', '我的提交'],
              ['catalog', '装备目录'],
              ['matching', '场景能力匹配'],
              ...(monitoringEnabled() ? ([['monitoring', '状态与维护']] as const) : []),
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={section === key}
              onClick={() => {
                // Leaving the section CLOSES the editor. Otherwise the revise form survives the tab
                // change and renders beside the create form, so the page offers two forms for two
                // different commands at once and an operator can submit the wrong one.
                setEditor(null);
                setSection(key);
                setSelected(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {auth && section === 'monitoring' && <EquipmentMonitoring key={auth.session} auth={live} />}
      {auth && section === 'matching' && <SynExactMatching key={auth.session} auth={auth} />}
      {auth && section === 'form' && scope && (
        <DeclarationForm
          live={live}
          scope={scope}
          products={products}
          initial={emptyDeclaration}
          submitLabel="保存草稿"
          onSave={(declaration) =>
            void live.run('保存装备草稿', async () => {
              const outcome = await live.port.create({
                session: live.session,
                idempotencyKey: newKey('asset-create'),
                declaration,
              });
              live.setNotice(outcomeText(outcome));
              if (outcome.kind === 'OK') {
                setOwn([outcome.value, ...own]);
                setSelected(outcome.value);
                setSection('mine');
              }
            })
          }
          onCancel={() => setSection('mine')}
        />
      )}
      {/*
        THE EDITOR BELONGS TO 我的提交 AND NOWHERE ELSE. Without the `section` term it survived a tab
        change and rendered beside the create form — two forms, two commands, one screen — which
        contradicts the invariant the detail/editor split below states and invites the wrong submit.
      */}
      {auth && editor && scope && section === 'mine' && (
        <DeclarationForm
          live={live}
          scope={scope}
          products={products}
          initial={toDeclarationInput(editor.declaration)}
          contentReference={{
            subjectId: editor.assetId,
            revision: editor.version,
            digest: editor.declaration.digest,
            view: 'draft',
          }}
          submitLabel="保存修订"
          onSave={(declaration) =>
            void live.run('保存装备修订', async () => {
              const outcome = await live.port.revise({
                session: live.session,
                idempotencyKey: newKey('asset-revise'),
                assetId: editor.assetId,
                expectedVersion: editor.version,
                declaration,
              });
              live.setNotice(outcomeText(outcome));
              if (outcome.kind === 'OK') {
                setEditor(null);
                setSelected(outcome.value);
                setOwn(
                  own.map((item) =>
                    item.assetId === outcome.value.assetId ? outcome.value : item,
                  ),
                );
                setSection('mine');
              }
            })
          }
          onCancel={() => setEditor(null)}
        />
      )}
      {auth && section === 'form' && !scope && !editor && (
        <p role="status">会话未解析出机构范围；未取得装备登记授权。</p>
      )}
      {auth && section === 'mine' && !selected && (
        <section aria-label="我的提交">
          <h3>我的提交</h3>
          <p>本机构登记的装备（含草稿）。草稿不会出现在装备目录中。</p>
          {own.length === 0 ? (
            <p>本次读取没有返回任何装备记录。</p>
          ) : (
            <ul className="asset-ux__rows">
              {own.map((asset) => (
                <li key={asset.assetId}>
                  <div>
                    <strong>{asset.declaration.displayName}</strong>
                    <p>
                      {STATE_LABELS[asset.state]} · 聚合版本 {asset.version} ·{' '}
                      {LINK_LABELS[asset.declaration.productLink.kind]}
                    </p>
                    <code>{asset.assetId}</code>
                  </div>
                  <button
                    type="button"
                    data-syn-asset-row={asset.assetId}
                    onClick={() => {
                      setSelected(asset);
                    }}
                  >
                    查看资料与版本
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {/*
        THE DETAIL AND THE EDITOR ARE ONE VIEW OR THE OTHER, NEVER BOTH. They are two renderings of
        the same asset, and showing them together puts two controls with the same meaning on screen —
        an "edit" button beside an open editor, and a "submit" button beside a form that has not been
        saved yet. Gating the detail on the editor being closed makes the page say one thing at a time.
      */}
      {auth && section === 'mine' && selected && editor === null && (
        <AssetDetail
          live={live}
          asset={selected}
          scope={scope ?? { country: '', agency: '', reviewScope: '' }}
          reviews={reviews}
          onChanged={(asset) => {
            setSelected(asset);
            setOwn(own.map((item) => (item.assetId === asset.assetId ? asset : item)));
          }}
          onEdit={setEditor}
          onBack={() => setSelected(null)}
        />
      )}
      {auth && section === 'catalog' && (
        <section aria-label="装备目录">
          <h3>装备目录（已发布）</h3>
          <p>仅本机构已发布的装备。草稿与审核中的资料不在此列。</p>
          {catalog.length === 0 ? (
            <p>本机构当前没有已发布装备。</p>
          ) : (
            <ul className="asset-ux__rows">
              {catalog.map((asset) => (
                <li key={asset.assetId}>
                  <div>
                    <strong>{asset.declaration.displayName}</strong>
                    <p>
                      已发布 v
                      {asset.publications.length > 0
                        ? asset.publications[asset.publications.length - 1]!.publishedVersion
                        : '—'}{' '}
                      · {asset.assetId}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-syn-catalog-row={asset.assetId}
                    onClick={() => {
                      setSelected(asset);
                      setSection('mine');
                    }}
                  >
                    查看详情
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {live.notice && (
        <p role="status" data-syn-notice>
          {live.notice}
        </p>
      )}
      <p>
        数据来源：REAL_LOCAL_SYNTHETIC_HTTP · NON_AUTHORITY ·
        机构范围来自服务端会话，不采信表单自报。资料发布不等于能力验证。
      </p>
    </section>
  );
}
